/* eslint-disable @nx/enforce-module-boundaries */
import { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useDebounce } from '@sweetspot/sweetspot-js/common/hooks/useDebounce'
import { useMutation } from 'react-query'
import { useToasts } from 'react-toast-notifications'
import { getMemberships } from '@sweetspot/club-portal-legacy/store/actions'
import { updateMembershipPriority } from '@sweetspot/sweetspot-js/features/memberships/services/api-platform'
import { FilterType } from '../types'
import { RootState } from '@sweetspot/club-portal-legacy/store/types'
import { useTranslation } from 'react-i18next'
import { TableSortItem } from '@sweetspot/scramble-ds'
import { useLocation } from 'react-router'
import { FILTER_QUERIES } from '../filters'
import { MembershipItem, SubscriptionType } from '@sweetspot/shared/types'

interface UseSubscriptionTableProps {
  subscriptionType: SubscriptionType
}

export const useSubscriptionTable = ({ subscriptionType }: UseSubscriptionTableProps) => {
  const [activeFilters, setActiveFilters] = useState<FilterType[]>(['ACTIVE'])
  const [sortItems, setSortItems] = useState<TableSortItem[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(20)
  const [searchString, setSearchString] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [localSubscriptions, setLocalSubscriptions] = useState<MembershipItem[]>([])

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { addToast } = useToasts()
  const location = useLocation()
  const debouncedSearchString = useDebounce(searchString, 500)

  const clubId = useSelector((state: RootState) => state?.golfClub?.selectedId)
  const shouldReload = (location.state as { shouldReload?: boolean })?.shouldReload || false

  const updateMembershipPriorityMutation = useMutation(
    ({
      membershipId,
      payload,
    }: {
      membershipId: string | number
      payload: { put_below?: string; put_above?: string }
    }) => updateMembershipPriority(membershipId, payload),
    {
      onError: () => {
        setLocalSubscriptions(localSubscriptions)
        addToast(t('toast.couldNotBeMoved'), { appearance: 'error' })
      },
    }
  )

  const {
    list: subscriptions,
    itemsPerPage,
    totalItems,
  } = useSelector((state: RootState) => state.membership)

  const fetchSubscriptions = useCallback(
    async (searchQuery = '', currentPage = 0, pageSize = 20) => {
      let query = `?page=${
        currentPage + 1
      }&limit=${pageSize}&club.id=${clubId}&type[]=${subscriptionType}`

      if (searchQuery) {
        query += `&name=${searchQuery}`
      }

      query += getFilterQuery(activeFilters)

      if (sortItems.length > 0) {
        sortItems.forEach(({ name, type }) => {
          if (type) {
            query += `&order[${name}.sort]=${type}`
          }
        })
      } else {
        query += `&order[priority]=desc`
      }

      try {
        await dispatch(getMemberships(query))
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch, clubId, activeFilters, sortItems, subscriptionType]
  )

  useEffect(() => {
    fetchSubscriptions(debouncedSearchString, currentPage, pageSize)
  }, [
    currentPage,
    debouncedSearchString,
    activeFilters,
    pageSize,
    fetchSubscriptions,
    itemsPerPage,
    sortItems,
    subscriptionType,
    shouldReload,
  ])

  useEffect(() => {
    setLocalSubscriptions(subscriptions)
  }, [subscriptions])

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(event.target.value)
    setCurrentPage(0)
    setIsLoading(true)
  }

  const getFilterQuery = (filters: FilterType[]) => {
    if (!filters || filters.length === 0) {
      return ''
    }
    return filters.map((filter) => FILTER_QUERIES[filter] || '').join('')
  }

  const onFilterChange = (filter: FilterType) => {
    setCurrentPage(0)
    if (!filter) {
      setActiveFilters([])
      setSearchString('')
      fetchSubscriptions('', 0)
      return
    }

    setActiveFilters((prevFilters) => {
      const updatedFilters = [...prevFilters]
      if (!updatedFilters.includes(filter as FilterType)) {
        updatedFilters.push(filter as FilterType)
      } else {
        updatedFilters.splice(updatedFilters.indexOf(filter as FilterType), 1)
      }
      return updatedFilters
    })
  }

  const onRowMoveHandler = ({
    activeId,
    overId,
  }: {
    activeId: string | number
    overId: string | number
  }) => {
    if (activeId && overId) {
      const activeIndex = localSubscriptions.findIndex((m) => m.id === activeId)
      const overIndex = localSubscriptions.findIndex((m) => m.id === overId)

      if (activeIndex === -1 || overIndex === -1) return

      const isMovingDown = activeIndex < overIndex

      const updatedData = [...localSubscriptions]
      const [movedItem] = updatedData.splice(activeIndex, 1)
      updatedData.splice(overIndex, 0, movedItem)

      setLocalSubscriptions(updatedData)

      const payload = {
        [isMovingDown ? 'put_below' : 'put_above']: `/api/memberships/${activeId}`,
      }

      updateMembershipPriorityMutation.mutate({ membershipId: overId, payload })
    }
  }

  return {
    activeFilters,
    sortItems,
    currentPage,
    pageSize,
    searchString,
    isLoading,
    subscriptions,
    totalItems,
    setCurrentPage,
    setPageSize,
    handleSearchChange,
    onFilterChange,
    onRowMoveHandler,
    setSortItems,
  }
}
